import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { fetchProducts, submitLead, trackApplyClick, formatTT } from "../lib/api";
import { CheckCircle2, Home as HomeIcon, Sparkles } from "lucide-react";

const MortgagePreQualDialog = ({ context, trigger }) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [mortgageProducts, setMortgageProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState("");
    const [matchedProduct, setMatchedProduct] = useState(null);
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "",
        employment_status: "", monthly_income: "", message: ""
    });

    const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    // Load mortgage products when the dialog opens
    useEffect(() => {
        if (!open || mortgageProducts.length > 0) return;
        setLoadingProducts(true);
        fetchProducts({ category: "mortgage" })
            .then((rows) => {
                // Sort: featured first, then lowest rate
                const sorted = [...(rows || [])].sort((a, b) => {
                    if ((b.featured ? 1 : 0) !== (a.featured ? 1 : 0)) {
                        return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
                    }
                    return a.interest_rate - b.interest_rate;
                });
                setMortgageProducts(sorted);
            })
            .catch(() => setMortgageProducts([]))
            .finally(() => setLoadingProducts(false));
    }, [open, mortgageProducts.length]);

    // Track click + sync matched product whenever user picks a lender
    useEffect(() => {
        if (!selectedProductId) {
            setMatchedProduct(null);
            return;
        }
        const p = mortgageProducts.find((x) => x.id === selectedProductId);
        setMatchedProduct(p || null);
        if (open && p) {
            trackApplyClick(p.id);
        }
    }, [selectedProductId, mortgageProducts, open]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!selectedProductId) {
            toast.error("Please choose a lender to apply with.");
            return;
        }
        setSubmitting(true);
        try {
            await submitLead({
                product_id: selectedProductId,
                full_name: form.full_name,
                email: form.email,
                phone: form.phone,
                employment_status: form.employment_status,
                monthly_income: Number(form.monthly_income) || 0,
                message: form.message,
                lead_source: "mortgage_calculator",
                mortgage_context: {
                    property_price: context.price,
                    down_payment: context.downPayment,
                    mortgage_amount: context.mortgageAmount,
                    monthly_payment: context.monthlyPayment,
                    interest_rate: context.rate,
                    term_years: context.years,
                    stamp_duty: context.stampDuty,
                    closing_costs: context.closingCosts,
                    cash_at_closing: context.cashAtClosing,
                    is_first_time_buyer: !!context.isFTB,
                },
            });
            setDone(true);
            toast.success("Pre-qualification request received");
        } catch (err) {
            toast.error("Could not submit. Check your details.");
        } finally {
            setSubmitting(false);
        }
    };

    const onOpenChange = (v) => {
        setOpen(v);
        if (!v) {
            setTimeout(() => {
                setDone(false);
                setForm({ full_name: "", email: "", phone: "", employment_status: "", monthly_income: "", message: "" });
            }, 200);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent
                className="max-w-xl bg-[#F8F7F4] border border-[#0B192C]/20 rounded-none max-h-[90vh] overflow-y-auto"
                data-testid="mortgage-prequal-dialog"
            >
                {done ? (
                    <div className="py-8 text-center">
                        <CheckCircle2 className="w-12 h-12 text-[#2F855A] mx-auto mb-4" strokeWidth={1.5} />
                        <h2 className="font-serif-display text-3xl mb-2">Pre-Qualification Sent</h2>
                        <p className="text-sm text-[#4A5568] mb-6 max-w-sm mx-auto">
                            Your request{matchedProduct ? <> for <strong>{matchedProduct.name}</strong></> : ""} has been sent
                            to {matchedProduct?.provider || "the lender"} along with your full calculator snapshot.
                            Expect a call within 24 hours.
                        </p>
                        <button
                            data-testid="prequal-close-btn"
                            onClick={() => onOpenChange(false)}
                            className="btn-primary px-6 py-2.5 label-overline"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <div className="label-overline text-[#0A4F49] mb-1 flex items-center gap-2">
                                <HomeIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                                Mortgage Pre-Qualification
                            </div>
                            <DialogTitle className="font-serif-display text-3xl tracking-tight">
                                Get pre-qualified
                            </DialogTitle>
                            <DialogDescription className="text-sm text-[#4A5568]">
                                We&rsquo;ll send your calculator results straight to your chosen T&amp;T lender.
                                No spam, no obligation.
                            </DialogDescription>
                        </DialogHeader>

                        {/* Calculator snapshot */}
                        <div className="border border-[#0B192C]/10 bg-white p-4 mt-3" data-testid="prequal-context-snapshot">
                            <div className="label-overline text-[#1E3A2B] mb-2 flex items-center gap-1.5">
                                <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Your snapshot
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                <div className="text-[#718096]">Property price</div>
                                <div className="font-mono-body text-right">{formatTT(context.price)}</div>
                                <div className="text-[#718096]">Down payment</div>
                                <div className="font-mono-body text-right">{formatTT(context.downPayment)}</div>
                                <div className="text-[#718096]">Mortgage needed</div>
                                <div className="font-mono-body text-right text-[#0A4F49]">{formatTT(context.mortgageAmount)}</div>
                                <div className="text-[#718096]">Term · rate</div>
                                <div className="font-mono-body text-right">{context.years} yr · {context.rate.toFixed(2)}%</div>
                                <div className="text-[#718096]">Est. monthly</div>
                                <div className="font-mono-body text-right">{formatTT(context.monthlyPayment)}</div>
                                {context.isFTB && (
                                    <>
                                        <div className="text-[#718096]">Status</div>
                                        <div className="font-mono-body text-right text-[#0A4F49]">First-time buyer</div>
                                    </>
                                )}
                            </div>
                        </div>

                        <form onSubmit={onSubmit} className="space-y-4 mt-4">
                            {/* Lender picker */}
                            <div>
                                <Label className="label-overline">Apply With Lender</Label>
                                <Select
                                    value={selectedProductId}
                                    onValueChange={setSelectedProductId}
                                    disabled={loadingProducts || mortgageProducts.length === 0}
                                >
                                    <SelectTrigger
                                        data-testid="prequal-lender-select"
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    >
                                        <SelectValue placeholder={loadingProducts ? "Loading lenders…" : "Choose a mortgage product"}>
                                            {matchedProduct
                                                ? `${matchedProduct.name} · ${matchedProduct.interest_rate}%${matchedProduct.featured ? " ★" : ""}`
                                                : null}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {mortgageProducts.map((p) => (
                                            <SelectItem key={p.id} value={p.id} textValue={`${p.name} ${p.interest_rate}%`}>
                                                {p.name} · {p.interest_rate}% {p.featured ? "★" : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {matchedProduct ? (
                                    <div className="text-xs text-[#718096] mt-1.5">
                                        {matchedProduct.provider} · max {formatTT(matchedProduct.max_amount)} · up to {Math.round((matchedProduct.max_tenure_months || 0) / 12)} yr
                                    </div>
                                ) : mortgageProducts.length > 0 ? (
                                    <div className="text-xs text-[#1E3A2B] mt-1.5">
                                        Tip — {mortgageProducts[0].name} currently has the lowest rate at {mortgageProducts[0].interest_rate}%.
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <Label className="label-overline">Full Name</Label>
                                <Input
                                    required
                                    data-testid="prequal-full-name"
                                    value={form.full_name}
                                    onChange={(e) => update("full_name", e.target.value)}
                                    className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="label-overline">Email</Label>
                                    <Input
                                        type="email" required
                                        data-testid="prequal-email"
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    />
                                </div>
                                <div>
                                    <Label className="label-overline">Phone</Label>
                                    <Input
                                        required
                                        data-testid="prequal-phone"
                                        value={form.phone}
                                        onChange={(e) => update("phone", e.target.value)}
                                        placeholder="+1 868 ..."
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="label-overline">Employment</Label>
                                    <Select value={form.employment_status} onValueChange={(v) => update("employment_status", v)}>
                                        <SelectTrigger
                                            data-testid="prequal-employment"
                                            className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                        >
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="employed">Employed</SelectItem>
                                            <SelectItem value="self-employed">Self-Employed</SelectItem>
                                            <SelectItem value="contract">Contract</SelectItem>
                                            <SelectItem value="retired">Retired</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="label-overline">Monthly Income (TT$)</Label>
                                    <Input
                                        type="number"
                                        data-testid="prequal-income"
                                        value={form.monthly_income}
                                        onChange={(e) => update("monthly_income", e.target.value)}
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="label-overline">Notes (optional)</Label>
                                <Textarea
                                    data-testid="prequal-message"
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    rows={2}
                                    placeholder="e.g. preferred contact time, property location…"
                                    className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting || !selectedProductId}
                                data-testid="prequal-submit-btn"
                                className="btn-accent w-full px-6 py-3 label-overline disabled:opacity-50"
                            >
                                {submitting ? "Submitting…" : "Send Pre-Qualification"}
                            </button>
                            <p className="text-xs text-[#718096] text-center leading-relaxed">
                                Your calculator snapshot (property price, down payment, term, est. monthly) is included with the lead.
                            </p>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MortgagePreQualDialog;
